import React, { useState, useContext } from 'react';
import {
  Box,
  IconButton,
  Container,
  Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import EmailIcon from '@mui/icons-material/Email';
import DescriptionIcon from '@mui/icons-material/Description';
import UserAdmin from '../administration/UserAdmin';
import FaceGroupingTester from '../administration/FaceGroupingTester';
import TenantConfigAdmin from '../administration/TenantConfigAdmin';
import DatasetManagement from '../administration/DatasetManagement';
import SenderRegistryAdmin from '../administration/SenderRegistryAdmin';
import WitnessStatementAdmin from '../administration/witnessStatements/WitnessStatementAdmin';
import UnifiedAppBar from '../components/navigation/UnifiedAppBar';
import UnifiedSettingsDialog from './dialogs/UnifiedSettingsDialog';
import SearchAppBar from './search/SearchAppBar';
import FilterDrawer from './search/FilterDrawer';
import SlugSearchResults from './people/SlugSearchResults';
import { useSettings } from '../contexts/settingsContext';
import { useSearch } from '../contexts/searchContext';
import { CognitoUserContext } from '../contexts/cognitoUserContext';
import { HistoryDrawerProvider } from '../contexts/historyDrawerContext';
import { useSearchPeople } from './search/useSearchPeople';
import {
  setLocalStorageEnumMap,
  clearLocalStorageEnumMap,
} from '../services/user/browserService';

/**
 * AdminApp - Administrator-only interface
 * Provides access to bulk data management operations + search for editing characteristics
 */
export default function AdminApp() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 = User Admin, 1 = Search, 2 = Face Grouping, 3 = Tenant Config, 4 = Datasets, 5 = Sender Registry, 6 = Witness Statements
  const [drawerOpen, setDrawerOpen] = useState(false);
  const settings = useSettings();

  const { cognitoUser } = useContext(CognitoUserContext);

  // Use unified search context instead of 3 separate contexts
  const {
    datasetMap,
    selectedDataset,
    enumMap,
    setEnumMap,
    searchString,
    setFilteredPeople,
    setMatchSource,
    selectedDatasets
  } = useSearch();

  const { searchPeople } = useSearchPeople(setFilteredPeople, setMatchSource);

  const myarr = Array.from(enumMap);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setLocalStorageEnumMap(enumMap);
    searchPeople(enumMap, searchString, selectedDatasets);
    setDrawerOpen(false);
  };

  const handleDrawerQuit = () => {
    setDrawerOpen(false);
  };

  const handleDrawerClear = () => {
    const clearMap = new Map();
    datasetMap.set(selectedDataset, clearMap);
    setEnumMap(clearMap);
    clearLocalStorageEnumMap();
  };

  // Build admin-specific left content (navigation tabs)
  const leftContent = (
    <>
      <IconButton
        color={activeTab === 0 ? "secondary" : "inherit"}
        onClick={() => setActiveTab(0)}
        title="User Admin"
      >
        <PeopleIcon />
      </IconButton>

      <IconButton
        color={activeTab === 1 ? "secondary" : "inherit"}
        onClick={() => setActiveTab(1)}
        title="Search & Edit"
      >
        <SearchIcon />
      </IconButton>

      <IconButton
        color={activeTab === 2 ? "secondary" : "inherit"}
        onClick={() => setActiveTab(2)}
        title="Face Grouping Tester"
      >
        <GroupWorkIcon />
      </IconButton>

      <IconButton
        color={activeTab === 3 ? "secondary" : "inherit"}
        onClick={() => setActiveTab(3)}
        title="Tenant Configuration"
      >
        <SettingsIcon />
      </IconButton>

      <IconButton
        color={activeTab === 4 ? "secondary" : "inherit"}
        onClick={() => setActiveTab(4)}
        title="Dataset Management"
      >
        <StorageIcon />
      </IconButton>

      <IconButton
        color={activeTab === 5 ? "secondary" : "inherit"}
        onClick={() => setActiveTab(5)}
        title="Email Sender Registry"
      >
        <EmailIcon />
      </IconButton>

      <IconButton
        color={activeTab === 6 ? "secondary" : "inherit"}
        onClick={() => setActiveTab(6)}
        title="Witness Statements"
      >
        <DescriptionIcon />
      </IconButton>

      {/* Show filter drawer button when in Search & Edit tab */}
      {activeTab === 1 && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={handleDrawerOpen}
          sx={{ ml: 2, ...(drawerOpen && { display: "none" }) }}
          title="Open Search Filters"
        >
          <SearchIcon />
        </IconButton>
      )}
    </>
  );

  return (
    <HistoryDrawerProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        {/* Unified App Bar - eliminates duplicate Settings and Logout buttons */}
        <UnifiedAppBar
          mode="admin"
          leftContent={leftContent}
          title="Sys Admin"
          onSettingsOpen={() => setSettingsOpen(true)}
          cognitoUser={cognitoUser}
        />

        <Container maxWidth="lg" sx={{ mt: 2, mb: 2, flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {activeTab === 0 && (
            <Paper sx={{ p: 2, flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <UserAdmin />
            </Paper>
          )}

          {activeTab === 1 && (
            <Box>
              <FilterDrawer
                open={drawerOpen}
                onClose={handleDrawerQuit}
                onApply={handleDrawerClose}
                onClear={handleDrawerClear}
                enumMap={enumMap}
              />

              <Box sx={{ mt: 2 }}>
                <SlugSearchResults />
              </Box>
            </Box>
          )}

          {activeTab === 2 && (
            <Paper sx={{ p: 2, flexGrow: 1, overflow: 'auto' }}>
              <FaceGroupingTester />
            </Paper>
          )}

          {activeTab === 3 && (
            <Paper sx={{ p: 2, flexGrow: 1, overflow: 'auto' }}>
              <TenantConfigAdmin />
            </Paper>
          )}

          {activeTab === 4 && (
            <Paper sx={{ p: 3, flexGrow: 1, overflow: 'auto' }}>
              <DatasetManagement />
            </Paper>
          )}

          {activeTab === 5 && (
            <Paper sx={{ p: 3, flexGrow: 1, overflow: 'auto' }}>
              <SenderRegistryAdmin />
            </Paper>
          )}

          {activeTab === 6 && (
            <Paper sx={{ p: 3, flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <WitnessStatementAdmin />
            </Paper>
          )}
        </Container>

      {/* Settings Dialog */}
      <UnifiedSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        cognitoUser={cognitoUser}
        // History settings
        deleteConfirmation={settings.deleteConfirmation}
        onDeleteConfirmationChange={settings.handleDeleteConfirmationChange}
        autoEnableVoice={settings.autoEnableVoice}
        onAutoEnableVoiceChange={settings.setAutoEnableVoice}
        stealthMode={settings.stealthMode}
        onStealthModeChange={settings.setStealthMode}
        visibleEventTypes={settings.visibleEventTypes}
        onVisibleEventTypesChange={settings.updateVisibleEventTypes}
        // Camera settings
        removeBg={settings.removeBg}
        setRemoveBg={settings.setRemoveBg}
        sliderValue={settings.sliderValue}
        setSliderValue={settings.setSliderValue}
        multiFaceMode={settings.multiFaceMode}
        setMultiFaceMode={settings.setMultiFaceMode}
        showBoundingBoxes={settings.showBoundingBoxes}
        setShowBoundingBoxes={settings.setShowBoundingBoxes}
        immediateFaceMatch={settings.immediateFaceMatch}
        setImmediateFaceMatch={settings.setImmediateFaceMatch}
        isIndoor={settings.isIndoor}
        setIsIndoor={settings.setIsIndoor}
        updateCharacteristicsOnMatch={settings.updateCharacteristicsOnMatch}
        setUpdateCharacteristicsOnMatch={settings.setUpdateCharacteristicsOnMatch}
        compressionQuality={settings.compressionQuality}
        setCompressionQuality={settings.setCompressionQuality}
        autoAdjustCompression={settings.autoAdjustCompression}
        setAutoAdjustCompression={settings.setAutoAdjustCompression}
        autoCreateEventOnAIMatch={settings.autoCreateEventOnAIMatch}
        setAutoCreateEventOnAIMatch={settings.setAutoCreateEventOnAIMatch}
        locationHighAccuracy={settings.locationHighAccuracy}
        setLocationHighAccuracy={settings.setLocationHighAccuracy}
        locationAcquisitionTimeout={settings.locationAcquisitionTimeout}
        setLocationAcquisitionTimeout={settings.setLocationAcquisitionTimeout}
        locationCacheMaxAge={settings.locationCacheMaxAge}
        setLocationCacheMaxAge={settings.setLocationCacheMaxAge}
        aggregatorEventLimit={settings.aggregatorEventLimit}
        setAggregatorEventLimit={settings.setAggregatorEventLimit}
      />
      </Box>
    </HistoryDrawerProvider>
  );
}
